/**
 * Board-finder quiz UI.
 *
 * All recommendation logic lives in assets/twt-quiz-recommend.js as a pure
 * function. This file is the shell around it: steps, state, rendering, cart and
 * analytics. Keep it that way — anything that influences which board is shown
 * belongs in the engine so it can be replayed over archived answers.
 */
if (!customElements.get('twt-quiz-modal')) {
  class TwtQuizModal extends HTMLElement {
    constructor() {
      super();
      this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', () => this.hide());
      this.addEventListener('keyup', (event) => {
        if (event.code.toUpperCase() === 'ESCAPE') this.hide();
      });
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }

    show(opener) {
      this.openedBy = opener;
      document.body.classList.add('overflow-hidden');
      this.setAttribute('open', '');
      trapFocus(this, this.querySelector('[role="dialog"]'));
    }

    hide() {
      document.body.classList.remove('overflow-hidden');
      this.removeAttribute('open');
      removeTrapFocus(this.openedBy);
    }
  }

  customElements.define('twt-quiz-modal', TwtQuizModal);
}

if (!customElements.get('twt-quiz')) {
  // Merchant creates this code (fixed $25 off) in Shopify admin — the quiz only
  // reveals it after email capture.
  const DISCOUNT_CODE = 'WELCOME25';

  // Bumped when the stored answer vocabulary changes, so a half-finished quiz
  // from the previous engine is discarded instead of replayed with dead values.
  const STATE_KEY = 'twt-quiz-state-v2';

  /**
   * Whether writing the quiz answers onto the cart (and therefore onto the
   * order) requires analytics consent.
   *
   * Set to `true` — the conservative reading. The trade-off is real and is a
   * legal call, not a technical one: in a store with a default-deny consent
   * banner this discards most of the calibration data, which is the entire
   * reason the answers are persisted (§12.2).
   *
   * The argument for `false`: these answers are volunteered by the customer to
   * get a recommendation, and are stored on their own order as the context for
   * that purchase — closer to order metadata than to tracking. Flip it only on
   * a deliberate decision about Law 25 / GDPR exposure.
   */
  const PERSIST_REQUIRES_CONSENT = true;

  /**
   * Whether the email gate submits the Shopify customer form natively.
   *
   * ON. Shopify guards /contact + form_type=customer with hCaptcha, which a
   * programmatic POST can never satisfy — the token is produced at submit time
   * and bound to the real form element. So a genuine submission is the only way
   * to subscribe, and it is targeted at a per-instance hidden iframe to keep the
   * customer on their results.
   *
   * Still unproven end to end: every test so far was confounded by browser
   * cache, the theme editor's sandboxed iframe, or localhost's bot wall. What
   * IS certain is that it cannot break the quiz — see the try/catch in
   * submitEmail. Turn this off if it ever proves otherwise.
   */
  const SUBMIT_SHOPIFY_CUSTOMER_FORM = true;

  /**
   * Display labels (§12.6). The stored taxonomy value stays canonical; only the
   * customer-facing string is translated here.
   */
  const ACTIVITY_LABELS = {
    'Fitness & Yoga': 'Yoga',
    Surf: 'Surfing',
    'Ocean & Coastal': 'Ocean',
    'Kids & Family': 'Family',
    'Camping & Cargo': 'Camping',
    'Kayak mode': 'Kayak',
  };


  /**
   * Customer-facing strings for everything the engine emits.
   *
   * assets/twt-quiz-recommend.js is a pure module by contract — no DOM, no
   * globals read at runtime — so it keeps emitting English identifiers
   * (card.slot, profile.archetype, profile.level, profile.activities) and its
   * Node tests assert on those. Translation therefore happens here, in the
   * rendering layer, keyed by those identifiers. The payload is rendered by
   * snippets/twt-quiz-i18n.liquid; every lookup falls back to the English the
   * engine produced, so a missing payload degrades rather than blanks the UI.
   */
  const I18N = (function () {
    const empty = {
      slot: {},
      level: {},
      activity: {},
      archetype: {},
      archetype_profile: {},
      accessory: {},
      reason: {},
      clause: {},
      ui: {},
    };
    const script = document.getElementById('twt-quiz-i18n');
    if (!script) return empty;
    // Liquid's `| t` HTML-escapes every non-_html key, so the payload arrives
    // holding "Fitness &amp; Yoga" and '{width}&quot; wide'. Decode once here:
    // every renderer below either sets textContent or escapes on insertion, so
    // leaving the entities in would print them literally.
    const decoder = document.createElement('textarea');
    const decode = (value) => {
      if (typeof value === 'string') {
        decoder.innerHTML = value;
        return decoder.value;
      }
      if (value && typeof value === 'object') {
        Object.keys(value).forEach((key) => {
          value[key] = decode(value[key]);
        });
      }
      return value;
    };
    try {
      return Object.assign(empty, decode(JSON.parse(script.textContent)));
    } catch (error) {
      return empty;
    }
  })();


  /**
   * The engine emits reason TOKENS ({ key, vars }) alongside its own English
   * rendering of them. When the payload carries a translated dictionary we
   * re-render the tokens through it; otherwise we keep the English the engine
   * already produced, per token, so a partial dictionary degrades one sentence
   * at a time.
   */
  function reasonList(card) {
    const engine = globalThis.TwtQuizRecommend;
    const english = card.reasons || [];
    if (!engine || !engine.renderReason || !Array.isArray(card.reason_tokens)) return english;
    return card.reason_tokens.map((token, index) => engine.renderReason(token, I18N) || english[index] || '');
  }

  function rationaleText(profile) {
    const engine = globalThis.TwtQuizRecommend;
    const english = (profile && profile.rationale) || '';
    if (!engine || !engine.renderRationale || !profile || !Array.isArray(profile.rationale_tokens)) return english;
    return engine.renderRationale(profile.rationale_tokens, I18N) || english;
  }

  function loadWarningText(card) {
    if (!card.load_warning) return '';
    return (card.load_warning_key && I18N.reason[card.load_warning_key]) || card.load_warning;
  }

  /** English slot label -> stable slot key, so card.label can be translated. */
  const SLOT_KEY_BY_LABEL = (function () {
    const labels = (globalThis.TwtQuizRecommend || {}).SLOT_LABELS || {};
    return Object.keys(labels).reduce((map, key) => {
      map[labels[key]] = key;
      return map;
    }, {});
  })();

  function slotLabel(label) {
    if (!label) return '';
    const key = SLOT_KEY_BY_LABEL[label];
    return (key && I18N.slot[key]) || label;
  }

  function activityLabel(activity) {
    return I18N.activity[activity] || ACTIVITY_LABELS[activity] || activity;
  }

  function archetypeHeadline(archetype, fallback) {
    return I18N.archetype[archetype] || fallback || '';
  }

  /** Accessory reasons are keyed by handle in the engine's ACCESSORY_RULES. */
  function accessoryReason(handle, fallback) {
    return I18N.accessory[handle] || fallback || '';
  }

  function loadData() {
    const script = document.getElementById('twt-quiz-data');
    if (!script) return { boards: [], accessories: {}, cart_handles: [] };
    try {
      const parsed = JSON.parse(script.textContent);
      return {
        boards: Array.isArray(parsed.boards) ? parsed.boards : [],
        accessories: parsed.accessories || {},
        cart_handles: Array.isArray(parsed.cart_handles) ? parsed.cart_handles : [],
      };
    } catch (error) {
      return { boards: [], accessories: {}, cart_handles: [] };
    }
  }

  /**
   * Analytics consent (§11). When the store has no consent banner configured the
   * customerPrivacy API is absent; we then behave like Shopify's own analytics
   * and treat tracking as allowed.
   */
  function hasAnalyticsConsent() {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return true;
    try {
      return privacy.analyticsProcessingAllowed();
    } catch (error) {
      return true;
    }
  }

  class TwtQuiz extends HTMLElement {
    connectedCallback() {
      this.data = loadData();
      this.steps = [...this.querySelectorAll('[data-step]')];
      this.questionSteps = this.steps.filter(
        (step) => !['results', 'no-match', 'manual-review', 'email'].includes(step.dataset.step)
      );
      this.answers = {};
      this.currentIndex = 0;
      this.completed = false;

      this.pill = this.querySelector('[data-quiz-pill]');
      this.backButton = this.querySelector('[data-quiz-back]');

      this.questionSteps.forEach((step) => {
        const isMulti = step.dataset.multi === 'true';
        const buttons = [...step.querySelectorAll('[data-value]')];

        buttons.forEach((button) => {
          button.addEventListener('click', () => {
            if (isMulti) {
              if (button.dataset.value === 'none') {
                buttons.forEach((other) => other.classList.toggle('is-selected', other === button));
              } else {
                button.classList.toggle('is-selected');
                buttons.find((other) => other.dataset.value === 'none')?.classList.remove('is-selected');
              }
              this.updateMultiContinue(step);
            } else {
              this.recordAnswer(step.dataset.question, button.dataset.value);
              this.goNext();
            }
          });
        });

        const continueButton = step.querySelector('[data-quiz-continue]');
        continueButton?.addEventListener('click', () => {
          const selected = buttons
            .filter((button) => button.classList.contains('is-selected'))
            .map((button) => button.dataset.value)
            .filter((value) => value !== 'none');
          this.recordAnswer(step.dataset.question, selected);
          this.showStep('email');
        });
      });

      this.backButton?.addEventListener('click', () => this.goBack());

      this.querySelectorAll('[data-quiz-retake]').forEach((button) => {
        button.addEventListener('click', () => this.reset());
      });

      const emailForm = this.querySelector('[data-quiz-email-form]');
      if (emailForm) {
        if (SUBMIT_SHOPIFY_CUSTOMER_FORM) this.prepareSubscribeSink(emailForm);
        emailForm.addEventListener('submit', (event) => this.submitEmail(event));
      }

      // Always open on question 1. Resuming a saved run meant anyone who had taken
      // the quiz before reopened straight onto their old last step — usually the
      // results — with no obvious way back to the start. reset() also clears the
      // stored answers, so a fresh run can never mix in values from the old one.
      this.reset();

      // Abandonment is the event everyone forgets and the one that pays: it says
      // which question makes people drop. Watch the total-weight step (Q4).
      this.closest('twt-quiz-modal')?.addEventListener('click', (event) => {
        if (event.target.id?.startsWith('ModalClose-')) this.trackAbandon('close');
      });
      window.addEventListener('pagehide', () => this.trackAbandon('pagehide'), { once: true });
    }

    // --- steps ------------------------------------------------------------

    showStep(stepKey) {
      this.steps.forEach((step) => step.setAttribute('hidden', ''));
      this.querySelector(`[data-step="${stepKey}"]`)?.removeAttribute('hidden');
      // The email gate is the last step before the results: it keeps a labelled
      // pill (an empty one read as broken) and a way back to the last question.
      // The results and the other end screens have neither.
      this.onEmailStep = stepKey === 'email';
      if (this.pill) this.pill.textContent = this.onEmailStep ? I18N.ui.last_step || 'Last step' : '';
      if (this.pill) this.pill.hidden = !this.onEmailStep;
      if (this.backButton) this.backButton.hidden = !this.onEmailStep;
    }

    render() {
      this.onEmailStep = false;
      this.steps.forEach((step) => step.setAttribute('hidden', ''));
      const activeStep = this.questionSteps[this.currentIndex];
      activeStep.removeAttribute('hidden');
      if (this.pill) {
        this.pill.hidden = false;
        this.pill.textContent = (I18N.ui.step_of || 'Step __CURRENT__ of __TOTAL__')
          .replace('__CURRENT__', this.currentIndex + 1)
          .replace('__TOTAL__', this.questionSteps.length);
      }
      if (this.backButton) this.backButton.hidden = this.currentIndex === 0;
    }

    goNext() {
      if (this.currentIndex < this.questionSteps.length - 1) {
        this.currentIndex += 1;
        this.saveState();
        this.render();
      }
    }

    goBack() {
      // From the email gate, back means the last question, which is still current.
      if (this.onEmailStep) {
        this.render();
        return;
      }
      if (this.currentIndex > 0) {
        this.currentIndex -= 1;
        this.render();
      }
    }

    reset() {
      this.answers = {};
      this.currentIndex = 0;
      this.completed = false;
      try {
        localStorage.removeItem(STATE_KEY);
      } catch (error) {
        /* storage unavailable — non-fatal */
      }
      this.render();
    }

    updateMultiContinue(step) {
      const continueButton = step.querySelector('[data-quiz-continue]');
      if (!continueButton) return;
      continueButton.disabled = step.querySelectorAll('.is-selected').length === 0;
    }

    recordAnswer(question, value) {
      const isFirstAnswer = Object.keys(this.answers).length === 0;
      this.answers[question] = value;
      this.saveState();
      if (isFirstAnswer) this.publish('twt:quiz-started');
      this.publish('twt:quiz-step', { quiz_step: question, quiz_value: value });
    }

    // --- state ------------------------------------------------------------

    saveState() {
      try {
        localStorage.setItem(
          STATE_KEY,
          JSON.stringify({ index: this.currentIndex, answers: this.answers })
        );
      } catch (error) {
        /* storage unavailable — non-fatal */
      }
    }


    // --- email gate --------------------------------------------------------

    submitEmail(event) {
      // When the native customer-form path is on we must NOT stop the default
      // action — Shopify's captcha handler listens on `document` and only sees
      // the submit if it bubbles. That leaves an exposure: this handler runs
      // validation, the engine and tag building, and a throw anywhere in there
      // would fall through to the form's default action and navigate the
      // customer away from their results. The catch closes it — whatever fails,
      // the page stays put and the quiz still finishes.
      try {
        if (!SUBMIT_SHOPIFY_CUSTOMER_FORM) event.preventDefault();
        const input = this.querySelector('[data-quiz-email-input]');
        const errorEl = this.querySelector('[data-quiz-email-error]');
        const email = input.value.trim();

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          event.preventDefault();
          errorEl.removeAttribute('hidden');
          input.setAttribute('aria-invalid', 'true');
          return;
        }
        errorEl.setAttribute('hidden', '');
        input.removeAttribute('aria-invalid');

        this.answers.email = email;
        this.answers.firstName = this.querySelector('[data-quiz-first-name]')?.value.trim() || '';
        this.answers.lastName = this.querySelector('[data-quiz-last-name]')?.value.trim() || '';
        this.saveState();

        const result = this.computeResult();

        // The tags have to be on the form BEFORE it submits, which is why the
        // recommendation is computed here rather than after. Shopify's captcha
        // handler listens on `document`, so it sees this submit only if we let it
        // bubble — calling preventDefault() here is what produced the 400s.
        const tagsInput = this.querySelector('[data-quiz-tags]');
        if (tagsInput && result) tagsInput.value = this.buildTags(result).join(',');

        if (!result) {
          event.preventDefault();
          this.showStep('no-match');
          return;
        }

        // The submit must never reach the browser's default action while the
        // customer form path is off — that is what navigated the page away.
        if (!SUBMIT_SHOPIFY_CUSTOMER_FORM) {
          this.reportSubscribe({ ok: false, status: 0, reason: 'customer_form_disabled', tags: [] });
          this.finish(result);
          return;
        }

        // Absolute guard: without a sink the native submit would navigate the whole
        // page to /contact and the customer would lose their results. The quiz is
        // never worth sacrificing for the newsletter subscribe — skip the
        // subscribe instead, and say so.
        if (!this.subscribeSink || this.subscribeSink.name !== event.target.target) {
          event.preventDefault();
          this.reportSubscribe({ ok: false, status: 0, reason: 'no_sink_skipped', tags: [] });
          this.finish(result);
          return;
        }

        this.watchSubscribe(tagsInput ? tagsInput.value.split(',') : []);
        this.finish(result);
      } catch (error) {
        event.preventDefault();
        console.error('[twt-quiz] submit failed, staying on the results', error);
        this.reportSubscribe({ ok: false, status: 0, reason: 'submit_threw', tags: [] });
        try {
          const result = this.computeResult();
          if (result) this.finish(result);
          else this.showStep('no-match');
        } catch (renderError) {
          this.showStep('no-match');
        }
      }
    }

    /**
     * Give this quiz instance its own submission sink.
     *
     * The quiz body renders twice on the homepage (modal popup + inline CTA
     * section). A hard-coded iframe name in Liquid produced two frames sharing
     * one name, and `target` resolves to the FIRST match in the document — so
     * the inline quiz posted into the popup's frame while its own watcher waited
     * for a load that never came. Unique names per instance remove that.
     */
    prepareSubscribeSink(form) {
      const uid = Math.random().toString(36).slice(2, 10);
      // Shopify names the form `contact_form` by default, so both instances on
      // the homepage share an id. Nothing here depends on it, but duplicate ids
      // are invalid and the next person to reach for getElementById would get
      // whichever instance happened to render first.
      form.id = `twt-quiz-subscribe-${uid}`;
      const name = `twt-quiz-sink-${uid}`;
      const sink = document.createElement('iframe');
      sink.name = name;
      sink.title = I18N.ui.newsletter_sink || 'Newsletter subscription';
      sink.tabIndex = -1;
      sink.setAttribute('aria-hidden', 'true');
      sink.setAttribute('data-quiz-subscribe-sink', '');
      sink.style.cssText = 'display:none;width:0;height:0;border:0';
      this.appendChild(sink);
      form.target = name;
      this.subscribeSink = sink;
    }

    /** Tags stamped on the Shopify customer, synced to Privy for segmentation. */
    buildTags(result) {
      const tags = ['newsletter', 'quiz'];
      const push = (prefix, value) => {
        if (value) tags.push(`${prefix}-${this.slugify(value)}`);
      };
      push('quiz-archetype', result.profile?.archetype);
      push('quiz-level', result.profile?.level);
      push('quiz-usecase', this.answers.use_case);
      // short_name, not the handle: handles run to 70 characters and read as
      // noise in the Shopify tag picker.
      push('quiz-board', result.boards?.find((card) => card.slot === 'best')?.short_name);
      return tags;
    }

    /**
     * The form posts into a hidden same-origin iframe, so the outcome is
     * readable from its resulting URL: Shopify redirects back with
     * ?customer_posted=true on success. Without this the subscribe fails
     * silently and the whole no-API-key CRM path dies unnoticed.
     */
    watchSubscribe(tags) {
      const sink = this.subscribeSink;
      if (!sink) return;
      const done = (outcome) => this.reportSubscribe({ ...outcome, tags });
      const timer = window.setTimeout(() => done({ ok: false, status: 0, reason: 'timeout' }), 15000);

      sink.addEventListener(
        'load',
        () => {
          window.clearTimeout(timer);
          let href = '';
          try {
            href = sink.contentWindow.location.href;
          } catch (error) {
            // Cross-origin means the captcha provider took over the frame.
            done({ ok: false, status: 0, reason: 'opaque_response' });
            return;
          }
          if (/customer_posted=true/.test(href)) {
            done({ ok: true, status: 200, reason: null });
            return;
          }
          // An iframe exposes no HTTP status, so identify the two failures that
          // actually happen by what the response body is. Shopify answers a
          // form POST from an unrecognised client (localhost, curl) with a
          // "Verifying your connection" interstitial — reporting that as a
          // generic rejection sends you hunting through your own code for a bug
          // that is not there.
          let title = '';
          try {
            title = sink.contentDocument?.title || '';
          } catch (error) {
            title = '';
          }
          const challenged = /verifying your connection|just a moment|attention required/i.test(title);
          done({
            ok: false,
            status: 0,
            reason: challenged ? 'bot_challenge_use_the_domain_preview' : 'shopify_rejected',
          });
        },
        { once: true }
      );
    }

    /**
     * Surface the subscribe outcome. Dispatched ungated (it is operational, not
     * tracking) and logged, so a broken CRM path is visible in the console and
     * in whatever listens to quiz events.
     */
    reportSubscribe(outcome) {
      // `via` is a self-identifying marker. Any build that predates the native
      // form submission cannot emit it, so its presence in the console proves
      // which code actually ran — no decoding line numbers against the source.
      outcome = { ...outcome, via: 'native-form' };
      if (!outcome.ok) {
        console.warn('[twt-quiz] newsletter subscribe failed', outcome);
      } else {
        console.info('[twt-quiz] newsletter subscribe ok', outcome);
      }
      this.dispatch('twt:quiz-subscribed', {
        quiz_subscribe_ok: outcome.ok,
        quiz_subscribe_status: outcome.status,
        quiz_subscribe_reason: outcome.reason,
        quiz_subscribe_tags: outcome.tags,
      });
    }

    slugify(text) {
      return String(text)
        // Decompose accents and drop the combining marks, so "Naïa 10'" becomes
        // "naia-10" rather than the unreadable "na-a-10" you get from stripping
        // the whole character. Board names are French-accented throughout.
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
    }

    // --- analytics (§11) ----------------------------------------------------

    /** Raw dispatch, no consent gate. Callers own the legal basis. */
    dispatch(name, detail) {
      document.dispatchEvent(new CustomEvent(name, { detail: { ...this.answers, ...detail } }));
    }

    /** Analytics dispatch — gated on tracking consent. */
    publish(name, detail) {
      if (!hasAnalyticsConsent()) return;
      this.dispatch(name, detail);
    }

    trackAbandon(source) {
      if (this.completed) return;
      if (!Object.keys(this.answers).length) return;
      const step = this.questionSteps[this.currentIndex];
      this.publish('twt:quiz-abandoned', {
        quiz_abandon_source: source,
        quiz_abandon_step: step?.dataset.question || null,
        quiz_abandon_step_index: this.currentIndex + 1,
      });
    }

    // --- result -------------------------------------------------------------

    /**
     * Run the engine. Split out of finish() because the recommendation has to
     * exist before the subscribe form submits — its tags are derived from it.
     * Returns null when the engine failed to load; never guesses a board.
     */
    computeResult() {
      const engine = window.TwtQuizRecommend;
      if (!engine) return null;
      return engine.recommend(this.answers, this.data.boards, {
        accessories: this.data.accessories,
        cartHandles: this.data.cart_handles,
      });
    }

    finish(result) {
      this.completed = true;

      this.persistToCart(result);

      const completion = {
        quiz_status: result.status,
        quiz_archetype: result.profile.archetype,
        quiz_level: result.profile.level,
        quiz_activities: result.profile.activities,
        quiz_glide_index: result.profile.glide_index,
        quiz_water: this.answers.water,
        quiz_use_case: this.answers.use_case,
        quiz_total_load_band: this.answers.total_load,
        quiz_priority: this.answers.priority,
        quiz_experience: this.answers.experience,
        quiz_primary_board: result.boards.find((card) => card.slot === 'best')?.handle || null,
        quiz_alternative_boards: result.boards
          .filter((card) => card.slot === 'alternative')
          .map((card) => card.handle),
        quiz_budget_board: result.boards.find((card) => card.slot === 'budget')?.handle || null,
        quiz_accessory_interests: this.answers.accessory_interests || [],
        quiz_recommended_accessories: result.accessories.map((item) => item.handle),
      };

      // Two destinations, two legal bases, deliberately independent events.
      //
      // Analytics is third-party tracking and is gated on tracking consent.
      // The CRM forward is not: the email gate asks for an address in order to
      // send results and board tips, which is an explicit marketing opt-in, and
      // no email means no CRM event at all. Routing the CRM through the
      // analytics event would silently make the opt-in they DID give depend on
      // a consent flag that governs something else.
      this.publish('twt:quiz-completed', completion);
      if (this.answers.email) this.dispatch('twt:quiz-crm-completed', completion);

      this.renderResult(result);
    }

    /**
     * Persist the raw answers server-side (§12.2).
     *
     * Cart attributes are the only durable server-side store a theme has, and
     * they are the right one here: Shopify copies them onto the ORDER, which
     * gives the answers <-> purchase join for free. That join is precisely what
     * §11 says is the prerequisite for recalibrating TIER_WEIGHT — conversion
     * broken down by quiz_archetype and quiz_primary_board.
     *
     * Limit to be honest about: a respondent who never checks out leaves only an
     * ephemeral cart, so their answers are not durably stored. The funnel for
     * those people comes from the analytics events instead. Capturing every
     * non-converting respondent still needs a real datastore.
     */
    persistToCart(result) {
      if (PERSIST_REQUIRES_CONSENT && !hasAnalyticsConsent()) return;

      const engine = window.TwtQuizRecommend;
      const encoded = engine.encodeAnswers(this.answers);
      if (!encoded) return;

      const attributes = {
        quiz_answers: encoded,
        quiz_engine_version: engine.ENGINE_VERSION,
        quiz_status: result.status,
        quiz_archetype: result.profile.archetype,
        quiz_level: result.profile.level,
        quiz_glide_index: result.profile.glide_index == null ? '' : String(result.profile.glide_index),
        quiz_completed_at: new Date().toISOString(),
        quiz_primary_board: result.boards.find((card) => card.slot === 'best')?.handle || '',
        quiz_alternative_board: result.boards.find((card) => card.slot === 'alternative')?.handle || '',
        quiz_budget_board: result.boards.find((card) => card.slot === 'budget')?.handle || '',
      };

      fetch(`${this.dataset.cartUpdateUrl || '/cart/update'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
      }).catch(() => {
        /* best-effort — never block the result screen on storage */
      });
    }

    renderResult(result) {
      this.steps.forEach((step) => step.setAttribute('hidden', ''));
      if (this.pill) this.pill.textContent = '';
      if (this.backButton) this.backButton.hidden = true;

      if (result.status === 'no_match') {
        this.querySelector('[data-step="no-match"]').removeAttribute('hidden');
        return;
      }

      if (result.status === 'manual_review') {
        const step = this.querySelector('[data-step="manual-review"]');
        step.removeAttribute('hidden');
        const title = step.querySelector('[data-review-profile-title]');
        if (title && result.profile.headline)
          title.textContent = archetypeHeadline(result.profile.archetype, result.profile.headline);
        return;
      }

      this.querySelector('[data-step="results"]').removeAttribute('hidden');
      this.querySelector('[data-result-profile-title]').textContent = archetypeHeadline(
        result.profile.archetype,
        result.profile.headline
      );
      this.querySelector('[data-result-reason]').textContent = rationaleText(result.profile);

      this.renderActivities(result.profile.activities);
      this.renderCards(result);
      this.renderDiscount();
      this.renderAccessories(result.accessories);
    }

    renderActivities(activities) {
      const wrap = this.querySelector('[data-result-activities]');
      const list = this.querySelector('[data-result-activity-list]');
      if (!wrap || !list) return;
      list.innerHTML = '';
      if (!activities?.length) {
        wrap.setAttribute('hidden', '');
        return;
      }
      wrap.removeAttribute('hidden');
      activities.forEach((activity) => {
        const item = document.createElement('li');
        item.textContent = activityLabel(activity);
        list.appendChild(item);
      });
    }

    renderCards(result) {
      const container = this.querySelector('[data-result-cards]');
      container.innerHTML = '';

      result.boards.forEach((card) => {
        const el = document.createElement('article');
        // All three are shown at once. The slot modifier keeps the primary
        // recommendation visually first among equals — without it, three
        // identical cards read as three equally-good options, which is not what
        // the engine actually said.
        el.className = `twt-quiz-result-card twt-quiz-result-card--${card.slot}`;
        el.innerHTML = `
          <span class="twt-quiz-result-card__badge">${this.escape(slotLabel(card.label))}</span>
          <div class="twt-quiz-result-card__body">
            <div class="twt-quiz-result-card__media">${
              card.image ? `<img src="${this.escape(card.image)}" alt="" loading="lazy">` : ''
            }</div>
            <div class="twt-quiz-result-card__info">
              <h4>${this.escape(card.short_name || card.title || '')}</h4>
              <p class="twt-quiz-result-card__price">${this.formatMoney(card.price_cad)}</p>
              <ul class="twt-quiz-result-card__reasons">${reasonList(card)
                .map((reason) => `<li>${this.escape(reason)}</li>`)
                .join('')}</ul>
              ${
                card.load_warning
                  ? `<p class="twt-quiz-result-card__warning">${this.escape(loadWarningText(card))}</p>`
                  : ''
              }
              <div class="twt-quiz-result-card__actions">
                <button
                  type="button"
                  class="twt-quiz-result-card__add"
                  data-add-handle="${this.escape(card.handle)}"
                  data-variant-id="${this.escape(card.variant_id)}"
                >
                  ${this.escape(I18N.ui.add_to_cart || 'Add to cart')}
                </button>
                <a class="twt-quiz-result-card__view" href="${this.escape(
                  this.decorateUrl(card.url, result.profile.archetype)
                )}">${this.escape(I18N.ui.view_board || 'View the board')}</a>
              </div>
            </div>
          </div>
        `;
        el.querySelector('[data-add-handle]')?.addEventListener('click', (event) =>
          this.addToCart(event.currentTarget)
        );
        el.querySelector('.twt-quiz-result-card__view')?.addEventListener(
          'click',
          () => this.publish('twt:quiz-product-click', { quiz_clicked_handle: card.handle }),
          { once: true }
        );

        container.appendChild(el);
      });


      const primaryCta = this.querySelector('[data-result-primary-cta]');
      const best = result.boards[0];
      if (primaryCta && best?.url) {
        primaryCta.href = this.decorateUrl(best.url, result.profile.archetype);
        primaryCta.addEventListener(
          'click',
          () => this.publish('twt:quiz-product-click', { quiz_clicked_handle: best.handle }),
          { once: true }
        );
      }
    }

    renderDiscount() {
      const codeEl = this.querySelector('[data-result-discount-code]');
      if (codeEl) codeEl.textContent = DISCOUNT_CODE;
      const copyButton = this.querySelector('[data-result-discount-copy]');
      if (!copyButton || copyButton.dataset.bound) return;
      copyButton.dataset.bound = 'true';
      copyButton.addEventListener('click', () => {
        navigator.clipboard?.writeText(DISCOUNT_CODE).then(() => {
          const original = copyButton.textContent;
          copyButton.textContent = I18N.ui.copied || 'Copied!';
          window.setTimeout(() => {
            copyButton.textContent = original;
          }, 1500);
        });
      });
    }

    renderAccessories(accessories) {
      const wrap = this.querySelector('[data-result-accessories]');
      const list = this.querySelector('[data-result-accessory-list]');
      list.innerHTML = '';

      if (!accessories.length) {
        wrap.setAttribute('hidden', '');
        return;
      }
      wrap.removeAttribute('hidden');

      accessories.forEach((item) => {
        const product = item.product || {};
        const el = document.createElement('div');
        el.className = 'twt-quiz-accessory';
        if (item.bundle) el.classList.add('twt-quiz-accessory--bundle');
        el.innerHTML = `
          <div class="twt-quiz-accessory__media">${
            product.image ? `<img src="${this.escape(product.image)}" alt="" loading="lazy">` : ''
          }</div>
          <div class="twt-quiz-accessory__info">
            <strong>${this.escape(product.title || item.handle)}</strong>
            <small>${this.escape(accessoryReason(item.handle, item.reason))}</small>
          </div>
          ${
            // Anything with a real choice must be picked on the product page —
            // never silently add a variant on the customer's behalf. The label
            // names the actual option: these products are one-size and vary by
            // colour, so "Choose size" sent people to look for something that
            // does not exist.
            item.requires_variant
              ? `<a class="twt-quiz-accessory__add" href="${this.escape(product.url || '#')}">Choose ${this.escape(
                  (item.variant_options && item.variant_options[0] ? item.variant_options[0] : 'option').toLowerCase()
                )}</a>`
              : `<button type="button" class="twt-quiz-accessory__add" data-add-handle="${this.escape(
                  item.handle
                )}" data-variant-id="${this.escape(product.variant_id)}">Add</button>`
          }
        `;
        list.appendChild(el);
      });

      list.querySelectorAll('[data-add-handle]').forEach((button) => {
        button.addEventListener('click', () => this.addToCart(button));
      });
    }

    // --- cart ---------------------------------------------------------------

    addToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId || variantId === 'null' || variantId === 'undefined') return;
      button.disabled = true;
      const original = button.textContent;
      button.textContent = 'Adding…';
      fetch(`${this.dataset.addUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      })
        .then((response) => {
          if (!response.ok) throw new Error('add failed');
          button.textContent = I18N.ui.added || 'Added ✓';
          this.data.cart_handles.push(button.dataset.addHandle);
          this.publish('twt:quiz-add-to-cart', { quiz_added_handle: button.dataset.addHandle });
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'twt-quiz' });
        })
        .catch(() => {
          button.textContent = original;
          button.disabled = false;
        });
    }

    // --- helpers ------------------------------------------------------------

    escape(value) {
      if (value == null) return '';
      return String(value).replace(/[&<>"']/g, (character) => {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character];
      });
    }

    formatMoney(amount) {
      if (typeof amount !== 'number') return '';
      return `$${amount.toFixed(2)}`;
    }

    /**
     * Propagate the discount, the archetype and any inbound UTMs so the result
     * click can be attributed (§3, §11).
     */
    decorateUrl(url, archetype) {
      if (!url) return '#';
      const target = new URL(url, window.location.origin);
      target.searchParams.set('discount', DISCOUNT_CODE);
      if (archetype) target.searchParams.set('quiz_archetype', this.slugify(archetype));
      new URLSearchParams(window.location.search).forEach((value, key) => {
        if (key.startsWith('utm_')) target.searchParams.set(key, value);
      });
      return target.pathname + target.search;
    }
  }

  customElements.define('twt-quiz', TwtQuiz);
}
